import {
  currentDialerSessionResponseSchema,
  dialerHeartbeatResponseSchema,
  dialerQueueSchema,
  dialerSessionResponseSchema,
  dialerSourceKey,
  dialerSummaryResponseSchema,
  savedDialerSessionResponseSchema,
  type AdvanceDialerBody,
  type DialerCommandBody,
  type DialerHeartbeatResponse,
  type DialerSessionView,
  type DialerSource,
  type DialerSummary,
  type SkipDialerBody,
  type StartDialerSessionBody,
} from "@gioia365/contracts";
import { queryOptions, skipToken } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../api/http";
import { tabKey, type TabContext, type TabKey } from "../session/tab-context";

// Le richieste del Power Dialer del browser (piano M5, Task 5; le rotte del server sono in `apps/server/src/dialer/routes.ts`):
// la sessione viva di chi guarda, l'avvio da una sorgente, i comandi della barra (avanzamento, salto, pausa, ripresa,
// interruzione), il battito, la coda e il riepilogo. Le chiavi stanno sotto quelle della scheda del browser (`["scheda",
// <scheda>, …]`): cambiare azienda o vista toglie anche la sessione letta.

const BASE = "/api/dialer-sessions";
const sessionPath = (sessionId: string) => `${BASE}/${encodeURIComponent(sessionId)}`;

export const dialerKeys = {
  current: (tab: TabKey) => ["scheda", tab, "dialer", "current"] as const,
  queue: (tab: TabKey, sessionId: string) => ["scheda", tab, "dialer", "queue", sessionId] as const,
  /** Le sessioni interrotte lette per sorgente: il prefisso di `saved`. */
  savedAll: (tab: TabKey) => ["scheda", tab, "dialer", "saved"] as const,
  /** La sessione interrotta di una sorgente (la sua chiave, `dialerSourceKey`): dai Task, piano M5, Task 21. */
  saved: (tab: TabKey, sourceKey: string) => ["scheda", tab, "dialer", "saved", sourceKey] as const,
};

/**
 * `GET /api/dialer-sessions/current`: la sessione attiva o in pausa di chi guarda (`null` senza), letta una volta all'avvio del
 * guscio; da lì la tengono aggiornata le risposte dei comandi, che la scrivono nella stessa chiave. Senza scheda niente.
 */
export function currentDialerQuery(tab: TabContext | null) {
  return queryOptions({
    queryKey: tab ? dialerKeys.current(tabKey(tab)) : (["dialer", "spento"] as const),
    queryFn: tab
      ? async ({ signal }) =>
          (
            await apiRequest("GET", `${BASE}/current`, {
              schema: currentDialerSessionResponseSchema,
              tab,
              signal,
            })
          ).session
      : skipToken,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/** `POST /api/dialer-sessions`: 201 per «Inizia a chiamare» e «Ricomincia da capo», 200 per «Riprendi a chiamare». */
export async function requestDialerStart(tab: TabContext, body: StartDialerSessionBody): Promise<DialerSessionView> {
  return (
    await apiRequest("POST", BASE, {
      schema: dialerSessionResponseSchema,
      body,
      tab,
    })
  ).session;
}

/** `GET /api/dialer-sessions/:id`: la sessione com'è adesso, anche interrotta o finita. */
export async function requestDialerSession(tab: TabContext, sessionId: string): Promise<DialerSessionView> {
  return (
    await apiRequest("GET", sessionPath(sessionId), {
      schema: dialerSessionResponseSchema,
      tab,
    })
  ).session;
}

/** `POST …/advance`, «Lead successivo», con la chiave del browser: lo stesso comando ripetuto risponde la stessa vista. */
export async function requestDialerAdvance(tab: TabContext, sessionId: string, body: AdvanceDialerBody): Promise<DialerSessionView> {
  return (
    await apiRequest("POST", `${sessionPath(sessionId)}/advance`, {
      schema: dialerSessionResponseSchema,
      body,
      tab,
    })
  ).session;
}

/** `POST …/skip`, «Salta questo Lead»: senza chiave, quindi mai ripetuto alla cieca. */
export async function requestDialerSkip(tab: TabContext, sessionId: string, body: SkipDialerBody): Promise<DialerSessionView> {
  return (
    await apiRequest("POST", `${sessionPath(sessionId)}/skip`, {
      schema: dialerSessionResponseSchema,
      body,
      tab,
    })
  ).session;
}

/** `POST …/pause` e `POST …/resume`: la sessione già nello stato chiesto risponde com'è. */
export async function requestDialerSwitch(tab: TabContext, sessionId: string, to: "pause" | "resume", body: DialerCommandBody): Promise<DialerSessionView> {
  return (
    await apiRequest("POST", `${sessionPath(sessionId)}/${to}`, {
      schema: dialerSessionResponseSchema,
      body,
      tab,
    })
  ).session;
}

/** `POST …/heartbeat`, ogni 60 secondi: rinnova la sessione e la riserva del Lead aperto. */
export function requestDialerHeartbeat(tab: TabContext, sessionId: string): Promise<DialerHeartbeatResponse> {
  return apiRequest("POST", `${sessionPath(sessionId)}/heartbeat`, {
    schema: dialerHeartbeatResponseSchema,
    tab,
  });
}

/** `POST …/interrupt`, «Interrompi»: risponde il riepilogo. */
export async function requestDialerInterrupt(tab: TabContext, sessionId: string, body: DialerCommandBody): Promise<DialerSummary> {
  return (
    await apiRequest("POST", `${sessionPath(sessionId)}/interrupt`, {
      schema: dialerSummaryResponseSchema,
      body,
      tab,
    })
  ).summary;
}

/** `GET …/summary`: il riepilogo di una sessione finita o interrotta altrove. */
export async function requestDialerSummary(tab: TabContext, sessionId: string): Promise<DialerSummary> {
  return (
    await apiRequest("GET", `${sessionPath(sessionId)}/summary`, {
      schema: dialerSummaryResponseSchema,
      tab,
    })
  ).summary;
}

/**
 * `GET …/queue`, «La coda della sessione»: letta a ogni apertura della finestra, mai in anticipo (è la lettura più pesante).
 * Senza scheda o senza sessione (la finestra chiusa) niente.
 */
export function dialerQueueQuery(tab: TabContext | null, sessionId: string | null) {
  return queryOptions({
    queryKey: tab && sessionId ? dialerKeys.queue(tabKey(tab), sessionId) : (["dialer", "coda", "spenta"] as const),
    queryFn:
      tab && sessionId
        ? ({ signal }) =>
            apiRequest("GET", `${sessionPath(sessionId)}/queue`, {
              schema: dialerQueueSchema,
              tab,
              signal,
            })
        : skipToken,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * `GET /api/dialer-sessions/saved?source=<chiave>`: la sessione interrotta di chi guarda su quella sorgente, per «Riprendi a
 * chiamare» (quando, quanti Lead fatti, da chi si riparte), `null` senza. Con `tab` o `source` nulli niente: le letture del Power
 * Dialer partono soltanto per chi chiama da quella scheda (mai nella «Guarda come…», ruling Q27). `queryKey` è la chiave di chi la
 * usa (la Smart List ha la sua); di partenza `dialerKeys.saved`.
 */
export function savedDialerSessionQuery(tab: TabContext | null, source: DialerSource | null, queryKey?: readonly unknown[]) {
  const sourceKey = source ? dialerSourceKey(source) : null;
  return queryOptions({
    queryKey: queryKey ?? (tab && sourceKey ? dialerKeys.saved(tabKey(tab), sourceKey) : (["dialer", "saved", sourceKey, "spenta"] as const)),
    queryFn:
      tab && sourceKey
        ? async ({ signal }) =>
            (await apiRequest("GET", `${BASE}/saved?${new URLSearchParams({ source: sourceKey }).toString()}`, { schema: savedDialerSessionResponseSchema, tab, signal })).saved
        : skipToken,
    // Rilegge l'intera sorgente sul server: all'apertura della pagina e quando la sessione della barra finisce, non a ogni ritorno
    // della finestra in primo piano.
    refetchOnWindowFocus: false,
  });
}

/**
 * «Interrompi e chiama da qui» per una sessione viva che la barra di questa scheda non tiene (nata in un'altra scheda del
 * browser): la sua versione letta adesso, poi `POST …/interrupt`; cambiata nel frattempo (`409 version_conflict`), una volta
 * ancora con quella nuova. Una sessione già ferma non si tocca.
 */
export async function interruptDialerSessionById(tab: TabContext, sessionId: string): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    const session = await requestDialerSession(tab, sessionId);
    if (session.state !== "active" && session.state !== "paused") return;
    try {
      await requestDialerInterrupt(tab, sessionId, { expectedVersion: session.version });
      return;
    } catch (error) {
      if (attempt > 0 || !(error instanceof ApiRequestError && error.code === "version_conflict")) throw error;
    }
  }
}
